import { useEffect, useState } from 'react';
import { useTracker } from '@archipro-website/tracker';
import { useCountryContext } from '@archipro-website/localisation';
import { ReactComponent as ShareIcon } from '@rocco/icons/svg/share.svg';
import { cn } from '@rocco/utils/cn';
import { detailCta, detailCtaIcon } from '~/ui-rocco/pages/detail-shared';
import { useUser } from '@modules/user';
import { useRootData } from '~/modules/root';
import { getUrlPrefix } from '~/modules/user/component/share-modal/util/getUrlPrefix';
import { addUtmDataToShareUrl } from '~/modules/user/component/share-modal/util/addUtmDataToShareUrl';
import {
    isNativeShareAvailable,
    type ShareOptions,
} from '~/modules/social-sharing/utils/sharing';
import { useShareTracking } from '~/modules/social-sharing/hooks/use-share-tracking';
import { ShareSheet } from '~/modules/moodboards/components/ShareSheet';

interface RevampShareButtonProps {
    /** The article's own path, e.g. `/article/my-article`. */
    shareLink: string;
    /** Article the share is attributed to — the UTM content id. */
    articleID: number;
    articleTitle: string;
    className?: string;
    /** Drop the label — for a bar with no room for one, e.g. the island. */
    iconOnly?: boolean;
    /** The picture a pin carries — the article's banner. */
    image?: string;
    /** Where the sheet opens; `above` for the island at the foot of the window. */
    sheetPlacement?: 'below' | 'above';
}

/**
 * The tiles' share sheet, hung from one Share button.
 *
 * The same box of targets a tile or a picture opens — copy link, Pinterest,
 * email and the networks — so sharing an article works as sharing anything
 * else on the site does. On a phone with an OS share sheet the button hands
 * the link straight to that instead. Carries the same `Share` event as the
 * networks' own buttons so the series stays continuous across the two article
 * pages.
 */
export const RevampShareButton = ({
    shareLink,
    articleID,
    articleTitle,
    className,
    iconOnly = false,
    image,
    sheetPlacement = 'below',
}: RevampShareButtonProps) => {
    const tracker = useTracker();
    const shareTracking = useShareTracking();
    const user = useUser();
    const { env } = useRootData();
    const { code } = useCountryContext();
    const [nativeShare, setNativeShare] = useState(false);
    // Decided on the client: the server has no navigator to ask.
    useEffect(() => setNativeShare(isNativeShareAvailable()), []);

    // The canonical public URL, not the current origin: a share from a branch
    // deploy or a preview host has to land on the live article.
    const shareUrl = shareLink.startsWith('http')
        ? shareLink
        : `${getUrlPrefix(code, env)}${shareLink}`;

    const share: ShareOptions = {
        url: addUtmDataToShareUrl({
            url: shareUrl,
            socialType: 'copyLink',
            pageID: articleID,
            user,
        }),
        title: articleTitle,
        image,
    };

    const logShare = () =>
        tracker.log('Share', {
            url: new URL(window.location.href),
            targetTracker: ['archiproTracker', 'ga4Tracker'],
        });

    // The platform's white button, not a copy of it: same kind, same size
    // step, same shadow as every other lifted control.
    const buttonClass = iconOnly
        ? detailCtaIcon('white-lifted', className)
        : detailCta('white-lifted', cn('gap-[0.625rem]', className));
    const buttonBody = (
        <>
            <ShareIcon
                aria-hidden="true"
                className="size-[1.125rem] fill-current"
            />
            {iconOnly ? null : <span>Share</span>}
        </>
    );

    if (nativeShare) {
        return (
            <button
                type="button"
                onClick={() => {
                    logShare();
                    shareTracking.logReveal();
                    void shareTracking.shareNative(share);
                }}
                aria-label={iconOnly ? 'Share' : undefined}
                className={buttonClass}
            >
                {buttonBody}
            </button>
        );
    }

    return (
        <ShareSheet share={share} placement={sheetPlacement}>
            {({ open, toggle }) => (
                <button
                    type="button"
                    aria-haspopup="menu"
                    aria-expanded={open}
                    onClick={() => {
                        if (!open) logShare();
                        toggle();
                    }}
                    aria-label={iconOnly ? 'Share' : undefined}
                    className={buttonClass}
                >
                    {buttonBody}
                </button>
            )}
        </ShareSheet>
    );
};
